import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMountTransition } from '../hooks/useMountTransition';
import { useSheetSwipeDismiss } from '../hooks/useSheetSwipeDismiss';
import { activateClose, dismissFocus, suppressGhostClick } from '../utils/dismissFocus';
import { LINKS } from '../constants/links';
import {
  formatNotifTime,
  noticeIconClass,
  noticeToneClass,
  resolveNoticeImageUrl,
} from '../utils/notifications';
import './styles/ConfirmDialog.css';

const MOBILE_MQ = '(max-width: 767px)';

const NoticeDialog = ({ open, notice, onClose, onOpenLink }) => {
  const { mounted, active } = useMountTransition(open, 320);
  const hasLink = Boolean((notice?.link || '').trim()) && (notice?.link || '').trim() !== '#';
  const imageUrl = resolveNoticeImageUrl(notice?.image, LINKS.eo);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ).matches : false
  );
  const sheet = isMobile;
  const sheetRef = useRef(null);
  const handleRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);

  const handleClose = useCallback(
    (event) => {
      dismissFocus(event);
      suppressGhostClick(420);
      onClose?.();
    },
    [onClose]
  );

  const swipeClose = useCallback(() => {
    suppressGhostClick(420);
    onClose?.();
  }, [onClose]);

  useSheetSwipeDismiss({
    sheetRef,
    handleRef,
    backdropRef,
    onDismiss: swipeClose,
    enabled: mounted && active && sheet,
  });

  useEffect(() => {
    if (!mounted) return undefined;
    document.body.classList.add('kkt-dialog-open');
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('kkt-dialog-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [mounted, onClose]);

  if (!mounted || typeof document === 'undefined' || !notice) return null;

  const tone = noticeToneClass(notice.level, notice.type);
  const isDialogType =
    notice?.type === 'meeting' ||
    notice?.type === 'parent_meeting' ||
    notice?.type === 'announcement';
  const isIntroLayout = isDialogType;

  return createPortal(
    <>
      <div
        ref={backdropRef}
        className={`kkt-blur-backdrop kkt-blur-backdrop--front${active ? ' is-visible' : ''}`}
        onClick={handleClose}
        role="presentation"
      />
      <div
        ref={sheetRef}
        className={`confirm-dialog confirm-dialog--notice${
          sheet ? ' confirm-dialog--sheet' : ''
        }${
          notice?.type === 'meeting' || notice?.type === 'parent_meeting'
            ? ' confirm-dialog--meeting'
            : notice?.type === 'announcement'
              ? ' confirm-dialog--announce'
              : ''
        }${isIntroLayout ? ' confirm-dialog--intro' : ''}${
          imageUrl ? ' confirm-dialog--has-photo' : ''
        }${active ? ' is-visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notice-dialog-title"
      >
        {sheet ? (
          <div ref={handleRef} className="confirm-dialog__handle" aria-hidden="true">
            <span className="confirm-dialog__grabber" />
          </div>
        ) : null}
        {/* Как в ЭО: иконка + заголовок, затем фото */}
        <div className="confirm-dialog__top">
          <div className={`confirm-dialog__icon ${tone}`} aria-hidden="true">
            <i className={`bi ${noticeIconClass(notice.icon, notice.type, notice.level)}`} />
          </div>
          <h2 id="notice-dialog-title" className="confirm-dialog__title">
            {notice.title}
          </h2>
        </div>
        {imageUrl ? (
          <img
            className="confirm-dialog__photo"
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
        ) : null}
        <p className="confirm-dialog__meta">{formatNotifTime(notice.createdAt)}</p>
        {notice.body ? <p className="confirm-dialog__text">{notice.body}</p> : null}
        <div
          className={`confirm-dialog__actions${hasLink ? '' : ' confirm-dialog__actions--one'}`}
        >
          {hasLink ? (
            <button
              type="button"
              className="confirm-dialog__btn confirm-dialog__btn--primary"
              onPointerDown={(event) => activateClose(event, () => onOpenLink?.(notice))}
              onClick={(event) => activateClose(event, () => onOpenLink?.(notice))}
            >
              Открыть
            </button>
          ) : null}
          <button
            type="button"
            className={`confirm-dialog__btn ${
              hasLink ? 'confirm-dialog__btn--ghost' : 'confirm-dialog__btn--primary'
            }`}
            onPointerDown={(event) => activateClose(event, handleClose)}
            onClick={(event) => activateClose(event, handleClose)}
          >
            Закрыть
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default NoticeDialog;
